/**
 * Structured pod-exit log payload. The control plane (workspace reconciler) can
 * only ever infer *that* an agent stopped — it sees a stale heartbeat and marks
 * the session Stranded/Ended. It cannot see *why*. This log closes that gap from
 * the pod side: emitted at every catchable exit path so the claudespace pod logs
 * record the cause (teardown signal vs clean finish vs crash) alongside the
 * `sessionId` that joins to the API's `session_stranded` / reconcile logs.
 *
 * Caveat: an OOMKill (SIGKILL / 137) is uncatchable, so it never reaches here —
 * those are diagnosed from GKE kernel OOM logs, not this line.
 */
// - signal: SIGTERM/SIGINT — teardown or a reaper asked us to stop
// - clean: runner.run() resolved normally
// - error: runner finalState=error, or run() rejected
// - force_timeout: graceful-shutdown watchdog fired
export type AgentExitReason =
  | "signal"
  | "clean"
  | "error"
  | "uncaught_exception"
  | "unhandled_rejection"
  | "force_timeout";

export interface AgentExitIdentity {
  sessionId?: string | undefined;
  taskId?: string | undefined;
  workspaceId?: string | undefined;
  runnerMode?: string | undefined;
}

export interface AgentExitLog extends AgentExitIdentity {
  event: "agent_exit";
  reason: AgentExitReason;
  exitCode: number;
  signal?: string;
  finalState?: string;
  uptimeSec: number;
}

/** Clean shutdowns are routine (info); every other exit is worth a warn so it
 * surfaces in severity-filtered log queries. */
export function exitLogLevel(reason: AgentExitReason): "info" | "warn" {
  return reason === "clean" ? "info" : "warn";
}

/**
 * Build the `agent_exit` payload, dropping any undefined optional/identity keys
 * so the serialized log line stays tidy (no `"workspaceId":undefined` noise).
 */
export function buildAgentExitLog(args: {
  reason: AgentExitReason;
  exitCode: number;
  uptimeSec: number;
  signal?: string | undefined;
  finalState?: string | undefined;
  identity?: AgentExitIdentity | undefined;
}): AgentExitLog {
  const log: AgentExitLog = {
    event: "agent_exit",
    reason: args.reason,
    exitCode: args.exitCode,
    uptimeSec: Math.round(args.uptimeSec * 10) / 10,
  };
  if (args.signal !== undefined) log.signal = args.signal;
  if (args.finalState !== undefined) log.finalState = args.finalState;
  const id = args.identity;
  if (id) {
    if (id.sessionId !== undefined) log.sessionId = id.sessionId;
    if (id.taskId !== undefined) log.taskId = id.taskId;
    if (id.workspaceId !== undefined) log.workspaceId = id.workspaceId;
    if (id.runnerMode !== undefined) log.runnerMode = id.runnerMode;
  }
  return log;
}
